import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { eventsApi } from '@/api/endpoints/events';
import { EventCard } from '@/components/domain';
import { SEOHead } from '@/components/seo';
import { PageSpinner, Button, Input, ButtonGroup, ToggleButton, LocationPermissionPrompt, LocationErrorPrompt, NoAreaCoverage } from '@/components/ui';
import { Search, Calendar, MapPin, Clock } from 'lucide-react';
import { isActiveOnDate } from '@/lib/rrule';
import { getLocationSEO } from '@/lib/seoUtils';
import { useFilters, metersToMiles } from '@/contexts/FilterContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAreaCoverage } from '@/hooks/useAreaCoverage';
import { getTimeForSort, type EventWithBar } from '../utils/eventSortUtils';

type SortMode = 'distance' | 'time';

/**
 * Check if an event occurs on a given date
 * - Recurring events: use rrule to check
 * - One-time events: check if startDatetime matches the date
 */
function isEventOnDate(event: EventWithBar, date: string): boolean {
  // If event has rrule, use rrule logic
  if (event.rrule) {
    return isActiveOnDate(event.rrule, date);
  }

  // For one-time events, check if startDatetime matches the selected date
  if (event.startDatetime) {
    const eventDate = event.startDatetime.split('T')[0];
    return eventDate === date;
  }

  // No rrule and no startDatetime - show on all dates
  return true;
}

export function EventsPage() {
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('distance');
  const { date, lat, lng, radius, hasValidLocation, locationStatus, locationLabel, locationSource } = useFilters();
  const { hidden } = useUserPreferences();
  const { hasBarCoverage, isCheckingCoverage } = useAreaCoverage();

  // Fetch all events for the radius (filtering done client-side)
  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['events', { lat, lng, radius }],
    queryFn: () => eventsApi.list({ lat: lat!, lng: lng!, radius, limit: 500, offset: 0 }),
    enabled: hasValidLocation,
  });

  // All events from API
  const allEvents = data?.events ?? [];

  // Filter out hidden bars, then by date and search client-side
  const searchLower = search.toLowerCase();
  const filteredEvents = allEvents
    .filter((e) => {
      // Hidden bar filter
      if (hidden.has(e.barId!)) return false;

      // Date filter
      if (!isEventOnDate(e, date)) return false;

      // Search filter (name, description, bar name)
      if (search) {
        const matchesName = e.name?.toLowerCase().includes(searchLower);
        const matchesDesc = e.description?.toLowerCase().includes(searchLower);
        const matchesBar = e.barName?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesDesc && !matchesBar) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const aTime = getTimeForSort(a.startDatetime);
      const bTime = getTimeForSort(b.startDatetime);

      if (sortMode === 'distance') {
        // Sort by distance (closest first), then by time as secondary sort
        const distanceDiff = (a.distance ?? Infinity) - (b.distance ?? Infinity);
        if (distanceDiff !== 0) return distanceDiff;
        return aTime.localeCompare(bTime);
      } else {
        // Sort by start time only (earliest first)
        return aTime.localeCompare(bTime);
      }
    });

  // Location guards
  if (!hasValidLocation) {
    if (locationStatus === 'denied') return <LocationPermissionPrompt />;
    if (locationStatus === 'unavailable') return <LocationErrorPrompt />;
    return <PageSpinner />;
  }

  if (isCheckingCoverage || isLoading) return <PageSpinner />;
  if (!hasBarCoverage) return <NoAreaCoverage />;

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Failed to load events</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        {...getLocationSEO(
          'Bar Events & Live Music Near You',
          `Discover ${filteredEvents.length} upcoming bar events including live music, trivia, and karaoke near you. Plan your night out.`,
          '/events',
          locationLabel,
          locationSource
        )}
      />
      <div className="space-y-4">
        {/* Search and sort controls */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" />
          <Input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Clear search */}
        {search && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearch('')}
          >
            Clear
          </Button>
        )}

        {/* Sort toggle */}
        <ButtonGroup>
          <ToggleButton
            isActive={sortMode === 'distance'}
            onClick={() => setSortMode('distance')}
          >
            <MapPin className="h-4 w-4" />
            Distance
          </ToggleButton>
          <ToggleButton
            isActive={sortMode === 'time'}
            onClick={() => setSortMode('time')}
          >
            <Clock className="h-4 w-4" />
            Time
          </ToggleButton>
        </ButtonGroup>
      </div>

      {/* Events list */}
      {filteredEvents.length === 0 ? (
        <div className="py-12 text-center">
          <Calendar className="mx-auto h-12 w-12 text-[var(--color-muted)] opacity-50" />
          <p className="mt-4 text-[var(--color-muted)]">
            No events found within {metersToMiles(radius)} miles
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {filteredEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
      </div>
    </>
  );
}
